import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { centsToPesos } from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';
import { DeliveryStatusBadge, StatusBadge, type TransactionDto } from './TransactionsPage';
import { MonitoringDetailModal, type TransactionDetailDto } from './MonitoringPage';

/**
 * Phase 2F — Delivery queue.
 *
 * Shows the READY/RELEASED orders designated for delivery whose delivery is
 * not closed yet, oldest received first (server-enforced). The detail is the
 * shared monitoring modal, which carries the delivery workflow panel
 * (assign → out for delivery → delivered → close) with in-app confirmation.
 *
 * Server-side: `GET /api/delivery?branch=…&q=…` — branch scope, search and
 * state are enforced by the API; this page only renders what it gets.
 */
export function DeliveryPage() {
  const { branchFilter } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<TransactionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Debounced search by number/customer (same as pick-up/monitoring).
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  // Detail with the delivery workflow panel.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TransactionDetailDto | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (query) params.set('q', query);

    void api
      .get<{ items: TransactionDto[] }>(`/delivery?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load deliveries.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, query, refreshKey]);

  // Detail (facts + processing/status history + delivery panel).
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    void api
      .get<{ transaction: TransactionDetailDto }>(`/delivery/${detailId}`)
      .then((res) => {
        if (!cancelled) setDetail(res.transaction);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetailError(err instanceof ApiError ? err.message : 'Failed to load the transaction.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [detailId, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);
  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;

  function formatWhen(iso: string): string {
    return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
  }

  return (
    <section className="space-y-4" aria-label="Delivery transactions">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Delivery Transactions</h1>
          <p className="mt-1 text-sm text-slate-500">
            {scopeLabel} · assign, dispatch, deliver and close — oldest received first.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={refresh}>
            <Icon name="refresh" className="h-5 w-5" />
            Refresh
          </Button>
          <Button variant="secondary" onClick={() => navigate('/admin/monitoring')}>
            <Icon name="monitoring" className="h-5 w-5" />
            Open Monitoring
          </Button>
        </div>
      </div>

      <Card className="flex items-start gap-3 p-4">
        <Icon name="shield" className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <p className="text-sm text-slate-500">
          Only <span className="font-semibold text-slate-700">READY</span> delivery orders appear
          here, and closed deliveries leave the queue automatically. Every step asks for
          confirmation, is recorded in the order&rsquo;s history and never changes payment state.
        </p>
      </Card>

      <Card className="p-4">
        <div className="relative">
          <Icon
            name="search"
            className="pointer-events-none absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search number or customer…"
            aria-label="Search deliveries"
            className={`${inputCls} pl-10`}
          />
        </div>
      </Card>

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-10 text-slate-500">
          <Spinner className="h-5 w-5 text-indigo-600" /> Loading deliveries…
        </div>
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-center p-8 text-center">
          <Icon name="delivery" className="h-8 w-8 text-slate-300" />
          <p className="mt-2 font-medium text-slate-700">
            {query ? 'No matching deliveries' : 'No transactions are in the delivery workflow right now.'}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {query
              ? 'Adjust the search to see more.'
              : 'Delivery orders appear here automatically once they are marked Ready.'}
          </p>
        </Card>
      ) : (
        <>
          <Card className="flex items-start gap-3 border-indigo-200 bg-indigo-50 p-4">
            <Icon name="delivery" className="mt-0.5 h-5 w-5 text-indigo-600" />
            <div>
              <p className="font-semibold text-indigo-900">
                {items.length} {items.length === 1 ? 'order' : 'orders'} in the delivery workflow
              </p>
              <p className="text-sm text-indigo-700">
                Oldest received first. Open an order to assign it, dispatch it, mark it delivered,
                then close it.
              </p>
            </div>
          </Card>

          {/* Desktop table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-3">Number</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Branch</th>
                  <th className="px-4 py-3">Address</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Payment</th>
                  <th className="px-4 py-3">Ready</th>
                  <th className="px-4 py-3">Delivery</th>
                  <th className="px-4 py-3" aria-label="Actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((tx) => (
                  <tr
                    key={tx.id}
                    onClick={() => setDetailId(tx.id)}
                    className="cursor-pointer hover:bg-slate-100/70"
                  >
                    <td className="px-4 py-3 font-semibold text-slate-900">{tx.number}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{tx.customerName}</p>
                      <p className="text-xs text-slate-500">{tx.service.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge>{tx.branch.code}</Badge>
                    </td>
                    <td className="max-w-56 px-4 py-3 text-slate-600">
                      {tx.deliveryAddress ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-slate-600">{tx.itemCount}</span>
                      {tx.hasDiscrepancy && (
                        <Badge tone="rose" className="ml-2">
                          Δ count
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {centsToPesos(tx.totalCents)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        tone={
                          tx.paymentStatus === 'PAID'
                            ? 'emerald'
                            : tx.paymentStatus === 'PARTIAL'
                              ? 'amber'
                              : 'rose'
                        }
                      >
                        {tx.paymentStatus}
                      </Badge>
                      {tx.paymentStatus !== 'PAID' && (
                        <p className="mt-0.5 text-xs font-semibold text-amber-700">
                          balance {centsToPesos(tx.totalCents - tx.amountPaidCents)}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {tx.readyAt ? formatWhen(tx.readyAt) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <DeliveryStatusBadge status={tx.deliveryStatus} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="secondary" onClick={() => setDetailId(tx.id)}>
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </>
      )}

      {/* Detail: loading / error / full modal with the delivery workflow panel */}
      {detailId && !detail && !detailError && (
        <Modal title="Transaction" onClose={() => setDetailId(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading transaction…
          </div>
        </Modal>
      )}
      {detailId && detailError && (
        <Modal title="Transaction" onClose={() => setDetailId(null)}>
          <ErrorBanner message={detailError} />
        </Modal>
      )}
      {detail && (
        <MonitoringDetailModal
          tx={detail}
          onClose={() => setDetailId(null)}
          onChanged={(next) => {
            setDetail(next);
            refresh();
          }}
        />
      )}
    </section>
  );
}
